import { useState } from 'react';
import { ArrowUpDown } from 'lucide-react';
import { Button } from './ui';

interface User {
  userId: string;
  email: string;
  subscription: {
    tier: 'trial' | 'pro';
    status: 'active' | 'cancelled' | 'expired';
    aiLimit: number;
    stripeSubscriptionId?: string | null;
  };
  aiUsed: number;
}

function isProGift(user: User): boolean {
  return user.subscription?.tier === 'pro' && !user.subscription?.stripeSubscriptionId;
}

interface UserTableProps {
  users: User[];
  onUserClick: (user: User) => void;
}

type SortField = 'email' | 'tier' | 'status' | 'aiUsed';
type SortDirection = 'asc' | 'desc';

export default function UserTable({ users, onUserClick }: UserTableProps) {
  const [sortField, setSortField] = useState<SortField>('email');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'email':
        aValue = a.email.toLowerCase();
        bValue = b.email.toLowerCase();
        break;
      case 'tier':
        aValue = a.subscription.tier;
        bValue = b.subscription.tier;
        break;
      case 'status':
        aValue = a.subscription.status;
        bValue = b.subscription.status;
        break;
      case 'aiUsed':
        aValue = a.aiUsed;
        bValue = b.aiUsed;
        break;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortButton = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 hover:text-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:ring-offset-1 rounded-lg px-1 py-0.5 transition-colors"
    >
      {children}
      <ArrowUpDown className="w-4 h-4 text-neutral-secondary" />
    </button>
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-border bg-white shadow-card">
      <table className="w-full">
        <thead className="bg-neutral-border/30">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-secondary uppercase tracking-wider">
              <SortButton field="email">Email</SortButton>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-secondary uppercase tracking-wider">
              <SortButton field="tier">Tier</SortButton>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-secondary uppercase tracking-wider">
              <SortButton field="status">Status</SortButton>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-secondary uppercase tracking-wider">
              <SortButton field="aiUsed">AI Used</SortButton>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-secondary uppercase tracking-wider">
              AI Limit
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-secondary uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-border">
          {sortedUsers.map((user, index) => (
            <tr
              key={user.userId}
              className={`cursor-pointer transition-colors ${
                index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-100'
              }`}
              onClick={() => onUserClick(user)}
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-primary">
                {user.email}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {user.subscription.tier === 'pro' ? (
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                      isProGift(user)
                        ? 'bg-violet-100 text-violet-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}
                    title={isProGift(user) ? 'Pro access granted as gift (admin)' : 'Pro via paid subscription'}
                  >
                    {isProGift(user) ? 'Pro (Gift)' : 'Pro (Subscription)'}
                  </span>
                ) : (
                  <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-neutral-border/50 text-neutral-primary">
                    Trial
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                  user.subscription.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : user.subscription.status === 'cancelled'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.subscription.status}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-primary">
                {user.aiUsed}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-primary">
                {user.subscription.aiLimit}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUserClick(user);
                  }}
                  className="h-auto py-1.5 px-2 text-brand-primary hover:bg-brand-primary/10"
                >
                  View Details
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sortedUsers.length === 0 && (
        <div className="text-center py-12 px-4">
          <p className="text-neutral-secondary font-medium">No users found</p>
          <p className="text-sm text-neutral-secondary mt-1">Try adjusting filters or search</p>
        </div>
      )}
    </div>
  );
}
